import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { usePromotionCodesList } from '@/lib/api/promotionCodes';
import { EditablePromotionCodesTable } from './editable-promotion-codes-table';

interface PromotionCodesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promotionId: string;
  storeId: string;
}

export const PromotionCodesDialog = ({
  open,
  onOpenChange,
  promotionId,
  storeId,
}: PromotionCodesDialogProps) => {
  const { t } = useTranslation('stores');

  const {
    data: promotionCodesResponse,
    isLoading,
    error,
  } = usePromotionCodesList(
    { promotionId, storeId, page: 1, limit: 100 },
    open,
  );

  const promotionCodes = promotionCodesResponse?.items || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[calc(100dvh-80px)] w-[calc(100vw-80px)] max-h-none max-w-none flex-col gap-0 p-0 sm:max-w-none md:max-w-none lg:max-w-none">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>
            {t('promotion.codes.dialog.title')}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <EditablePromotionCodesTable
              promotionCodes={promotionCodes}
              promotionId={promotionId}
              storeId={storeId}
            />
          )}
          {error && (
            <div className="rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
              {error instanceof Error
                ? error.message
                : t('loadPromotionCodesError')}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
